// API Configuration
export const API_BASE_URL =
  process.env['REACT_APP_API_URL'] || 'http://localhost:8000';
export const API_TIMEOUT = 30000; // 30 seconds

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    PERMISSIONS: '/auth/permissions',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
  },
  // Users
  USERS: {
    LIST: '/users',
    CREATE: '/users',
    GET: (id: string) => `/users/${id}`,
    UPDATE: (id: string) => `/users/${id}`,
    DELETE: (id: string) => `/users/${id}`,
    PROFILE: (id: string) => `/users/${id}/profile`,
  },
  // Teachers (filtered from users)
  TEACHERS: {
    LIST: '/auth/users', // Uses auth/users endpoint with role filter
    GET: (id: string) => `/auth/users/${id}`,
  },
  // Districts
  DISTRICTS: {
    LIST: '/districts',
    CREATE: '/districts',
    UPDATE: (id: string) => `/districts/${id}`,
    DELETE: (id: string) => `/districts/${id}`,
    SCHOOLS: (id: string) => `/districts/${id}/schools`,
    DATA_SHARING_CONSENT: (id: string) =>
      `/districts/${id}/data-sharing-consent`,
    AI_SCORING_ASSIST: (id: string) => `/districts/${id}/ai-scoring-assist`,
    AI_SCORING_AGREEMENT_METRICS: (id: string) =>
      `/districts/${id}/ai-scoring/agreement-metrics`,
    AI_SCORING_TRAINING_EXPORT: (id: string) =>
      `/districts/${id}/ai-scoring/training-examples/export`,
  },
  // Schools
  SCHOOLS: {
    LIST: '/schools',
    CREATE: '/schools',
    UPDATE: (id: string) => `/schools/${id}`,
    DELETE: (id: string) => `/schools/${id}`,
    TEACHERS: (id: string) => `/schools/${id}/teachers`,
  },
  // Evaluations
  EVALUATIONS: {
    LIST: '/evaluations',
    CREATE: '/evaluations',
    GET: (id: string) => `/evaluations/${id}`,
    UPDATE: (id: string) => `/evaluations/${id}`,
    DELETE: (id: string) => `/evaluations/${id}`,
    SUBMIT: (id: string) => `/evaluations/${id}/submit`,
    PROCESS_VIDEO: (id: string) => `/evaluations/${id}/process-video`,
    SCORE: (id: string) => `/evaluations/${id}/score`,
    SCORES: (id: string) => `/evaluations/${id}/scores`,
    REPORT: (id: string) => `/evaluations/${id}/report`,
    STATS: '/evaluations/stats/summary',
    AI_SCORING: {
      TRIGGER: (id: string) => `/evaluations/${id}/ai-scoring`,
      RUNS: (id: string) => `/evaluations/${id}/ai-scoring/runs`,
      RUN: (evaluationId: string, runId: string) =>
        `/evaluations/${evaluationId}/ai-scoring/runs/${runId}`,
      DRAFTS: (id: string) => `/evaluations/${id}/ai-scoring/drafts`,
      GENERATE_DRAFTS: (evaluationId: string, runId: string) =>
        `/evaluations/${evaluationId}/ai-scoring/runs/${runId}/drafts`,
    },
  },
  // Rubrics/Templates
  RUBRICS: {
    TEMPLATES: '/rubrics/templates',
    TEMPLATE_FULL: (id: string) => `/rubrics/templates/${id}/full`,
  },
  // Announcements
  ANNOUNCEMENTS: {
    LIST: '/announcements',
    CREATE: '/announcements',
    GET: (id: string) => `/announcements/${id}`,
    UPDATE: (id: string) => `/announcements/${id}`,
    DELETE: (id: string) => `/announcements/${id}`,
    BROADCAST: (id: string) => `/announcements/${id}/broadcast`,
    APPROVE: (id: string) => `/announcements/${id}/approve`,
    ANALYTICS: '/announcements/analytics/overview',
  },
} as const;

// Application Routes
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  UNAUTHORIZED: '/unauthorized',
  DASHBOARD: '/dashboard',
  DISTRICTS: '/districts',
  DISTRICT_DETAIL: '/districts/:id',
  SCHOOLS: '/schools',
  SCHOOL_DETAIL: '/schools/:id',
  USERS: '/users',
  USER_DETAIL: '/users/:id',
  EVALUATIONS: '/evaluations',
  EVALUATION_DETAIL: '/evaluations/:id',
  EVALUATION_SCORE: '/evaluations/:id/score',
  EVALUATION_REPORT: '/evaluations/:id/report',
  ANNOUNCEMENTS: '/announcements',
  ANNOUNCEMENT_DETAIL: '/announcements/:id',
  EVALUATIONS_CREATE: '/evaluations/create',
  PERMISSION_SLIPS: '/permission-slips',
  PERMISSION_SLIPS_CREATE: '/permission-slips/create',
  ANALYTICS: '/analytics',
  INTERVENTIONS: '/interventions',
  INTERVENTIONS_DASHBOARD: '/interventions/dashboard',
  REPORTS: '/reports',
  BUDGET: '/budget',
  INVENTORY: '/inventory',
  PARENT_PORTAL: '/parent-portal',
  SETTINGS: '/settings',
  PROFILE: '/profile',
} as const;

// UI Constants
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  MAX_VISIBLE_PAGES: 5,
} as const;

// Form Validation Patterns
export const VALIDATION_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^[+]?[1-9][\d\s\-()]{7,15}$/,
  PASSWORD:
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
  NAME: /^[a-zA-Z\s\-']{2,50}$/,
  ZIP_CODE: /^\d{5}(-\d{4})?$/,
} as const;

// Responsive Breakpoints (Tailwind CSS)
export const BREAKPOINTS = {
  SM: '640px',
  MD: '768px',
  LG: '1024px',
  XL: '1280px',
  '2XL': '1536px',
} as const;

// Role-based Permissions
export const PERMISSIONS = {
  SUPER_ADMIN: [
    'manage_districts',
    'manage_schools',
    'manage_users',
    'manage_evaluations',
    'view_reports',
    'manage_settings',
  ],
  DISTRICT_ADMIN: [
    'manage_schools',
    'manage_users',
    'manage_evaluations',
    'view_reports',
  ],
  SCHOOL_ADMIN: ['manage_users', 'manage_evaluations', 'view_reports'],
  EVALUATOR: ['create_evaluations', 'edit_evaluations', 'view_evaluations'],
  TEACHER: ['view_evaluations'],
} as const;

// Status Colors (Tailwind CSS classes)
export const STATUS_COLORS = {
  EXCELLENT: 'bg-green-100 text-green-800 border-green-200',
  PROFICIENT: 'bg-blue-100 text-blue-800 border-blue-200',
  DEVELOPING: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  UNSATISFACTORY: 'bg-red-100 text-red-800 border-red-200',
  NOT_EVALUATED: 'bg-gray-100 text-gray-800 border-gray-200',
  PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  COMPLETED: 'bg-green-100 text-green-800 border-green-200',
  DRAFT: 'bg-gray-100 text-gray-800 border-gray-200',
} as const;

// Unified backend-aligned permission names (snake_case)
export const APP_PERMISSIONS = {
  CREATE_USER: 'create_user',
  READ_USER: 'read_user',
  UPDATE_USER: 'update_user',
  DELETE_USER: 'delete_user',
  CREATE_DISTRICT: 'create_district',
  READ_DISTRICT: 'read_district',
  UPDATE_DISTRICT: 'update_district',
  DELETE_DISTRICT: 'delete_district',
  CREATE_SCHOOL: 'create_school',
  READ_SCHOOL: 'read_school',
  UPDATE_SCHOOL: 'update_school',
  DELETE_SCHOOL: 'delete_school',
  CREATE_EVALUATION: 'create_evaluation',
  READ_EVALUATION: 'read_evaluation',
  UPDATE_EVALUATION: 'update_evaluation',
  DELETE_EVALUATION: 'delete_evaluation',
  CONDUCT_EVALUATION: 'conduct_evaluation',
  VIEW_ALL_EVALUATIONS: 'view_all_evaluations',
  UPLOAD_VIDEO: 'upload_video',
  VIEW_ANALYTICS: 'view_analytics',
  VIEW_DISTRICT_ANALYTICS: 'view_district_analytics',
  VIEW_SCHOOL_ANALYTICS: 'view_school_analytics',
  VIEW_STATE_ANALYTICS: 'view_state_analytics',
  EXPORT_REPORTS: 'export_reports',
  SEND_ANNOUNCEMENTS: 'send_announcements',
  MANAGE_COMMUNICATIONS: 'manage_communications',
  MANAGE_SYSTEM_SETTINGS: 'manage_system_settings',
  VIEW_AUDIT_LOGS: 'view_audit_logs',
  MANAGE_INTEGRATIONS: 'manage_integrations',
  MANAGE_RESOURCES: 'manage_resources',
  VIEW_BUDGET: 'view_budget',
  APPROVE_EXPENSES: 'approve_expenses',
  // Permission Slips (independent CRUD)
  CREATE_PERMISSION_SLIP: 'create_permission_slip',
  READ_PERMISSION_SLIP: 'read_permission_slip',
  UPDATE_PERMISSION_SLIP: 'update_permission_slip',
  DELETE_PERMISSION_SLIP: 'delete_permission_slip',
  // Interventions (independent CRUD)
  CREATE_INTERVENTION: 'create_intervention',
  READ_INTERVENTION: 'read_intervention',
  UPDATE_INTERVENTION: 'update_intervention',
  DELETE_INTERVENTION: 'delete_intervention',
  // Parent Portal (independent CRUD-style for gating)
  CREATE_PARENT_PORTAL: 'create_parent_portal',
  READ_PARENT_PORTAL: 'read_parent_portal',
  UPDATE_PARENT_PORTAL: 'update_parent_portal',
  DELETE_PARENT_PORTAL: 'delete_parent_portal',
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN:
    'Access denied. You do not have permission to access this resource.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  SERVER_ERROR: 'An unexpected error occurred. Please try again later.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  SAVED: 'Changes saved successfully.',
  CREATED: 'Item created successfully.',
  UPDATED: 'Item updated successfully.',
  DELETED: 'Item deleted successfully.',
  LOGIN_SUCCESS: 'Login successful.',
  LOGOUT_SUCCESS: 'Logout successful.',
} as const;

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM dd, yyyy',
  INPUT: 'yyyy-MM-dd',
  FULL: 'MMMM dd, yyyy',
  SHORT: 'MM/dd/yyyy',
  TIME: 'HH:mm',
  DATETIME: 'MMM dd, yyyy HH:mm',
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_PREFERENCES: 'user_preferences',
  THEME: 'theme',
  LANGUAGE: 'language',
} as const;
